import { showError } from 'components/notify';
import { usePortal } from 'components/portal/portal';
import { sb, Sb } from 'utils/db';
import { NotNull, safeParseList } from 'shared/type';
import { useQuerySb } from 'utils/db/useQuerySb';
import { useState } from 'react';
import { showNotification } from '@mantine/notifications';
import { Notify } from './notify';
import { zNotifyRow, NotifyRow } from './notifyTypes';
import { match } from 'ts-pattern';
import { notifyProps } from './Notify';
import { useDocumentVisibility } from '@mantine/hooks';
import { useMantineTheme } from '@mantine/core';
export const useNotifys = (options?: {
  showGlobalNotifications?: boolean;
}) => {
  const {
    profile
  } = usePortal();
  const [opened, _setOpened] = useState(false);
  const [loadingReadAll, setLoadingReadAll] = useState(false);
  const [loadingClearAll, setLoadingClearAll] = useState(false);
  const documentState = useDocumentVisibility();
  const theme = useMantineTheme();
  const {
    data: notifys,
    error: error_0,
    setQueryData,
    refetch
  } = useQuerySb('notify', ['useNotifys', {
    profile_id: profile?.id
  }, {}], async ({
    queryKey: [,, {
      profile_id
    }]
  }) => {
    const {
      data,
      error
    } = await sb.from('notify').select('*').match({
      profile_id,
      archived: false
    });
    if (error) throw error;
    return data == null ? undefined : safeParseList(zNotifyRow, data);
  }, {
    onChange: options?.showGlobalNotifications ? p => {
      match(p).with({
        eventType: 'INSERT'
      }, async p_0 => {
        const id = p_0.new.id;
        showNotification(notifyProps({
          notify: notifyWithUse(sb, zNotifyRow.parse(p_0.new)),
          inline: false,
          showDate: false,
          theme
        }));
        if (!p_0.new.read && profile?.workspace_id && p_0.new.workspace_id == profile.workspace_id && documentState == 'visible') {
          await sb.from('notify').update({
            read: true
          }).match({
            id
          });
        }
      }).with({
        eventType: 'UPDATE'
      }, {
        eventType: 'DELETE'
      }, () => {
        refetch();
      }).exhaustive();
    } : undefined,
    enabled: !!profile,
    staleTime: Infinity
  });
  const clearAll = async () => {
    setLoadingClearAll(true);
    try {
      if (notifys) setQueryData([]);
      const {
        error
      } = await sb.from('notify').update({
        archived: true
      }).match({
        profile_id: profile?.id,
        archived: false
      });
      if (error) showError(error, 'error clearing notifications');
    } finally {
      setLoadingClearAll(false);
    }
  };
  const readAll = async () => {
    setLoadingReadAll(true);
    try {
      if (notifys) setQueryData(notifys.map(n => ({
        ...n,
        read: true
      })));
      const {
        error
      } = await sb.from('notify').update({
        read: true
      }).match({
        profile_id: profile?.id,
        read: false
      });
      if (error) showError(error, 'error clearing notifications');
    } finally {
      setLoadingReadAll(false);
    }
  };
  const setOpened = async (o: boolean) => {
    _setOpened(o);
  };
  const unread = notifys ? notifys.filter(n => !n.read).length : undefined;
  if (error_0) showError(error_0.message);
  return {
    notifys: notifys?.map(n_0 => notifyWithUse(sb, n_0)),
    clearAll,
    readAll,
    opened,
    setOpened,
    unread,
    loading: {
      clearAll: loadingClearAll,
      readAll: loadingReadAll
    }
  };
};
export type UseNotify = ReturnType<typeof useNotifys>;
export type UseNotifyRow = NotNull<UseNotify['notifys']>[number];
const notifyWithUse = (sb: Sb, row: NotifyRow) => ({
  ...row,
  clear: () => Notify.clear(sb, row.id),
  markRead: (read: boolean = true) => Notify.markRead(sb, row.id, read)
});